import {
  type DeadlineAiFieldProposal,
  type DeadlineAiProposal,
  type DeadlineRecurrenceChoice,
  readConfidentValue,
} from '@prospero/shared';

// Shared with the assistant's document reading: one rule for both (tested in shared).
export { buildDeadlineAiFieldProposal } from '@prospero/shared';

export type CreateDialogFillGroup = 'type' | 'recurrence';

export interface CreateDialogFillPlan {
  documentTypeId?: string;
  recurrence?: DeadlineRecurrenceChoice;
  dueAt?: string;
  validityMonths?: number;
}

/**
 * Cosa riempire nei dialog di creazione: solo i gruppi che l'utente non ha toccato.
 * La data si propone solo se la risposta non è "nessuna scadenza".
 */
export function planCreateDialogFill(
  fieldProposal: DeadlineAiFieldProposal,
  touched: Readonly<Record<CreateDialogFillGroup, boolean>>,
  hasChosenType: boolean,
): CreateDialogFillPlan {
  const plan: CreateDialogFillPlan = {};
  if (!(touched.type || hasChosenType) && fieldProposal.documentTypeId) {
    plan.documentTypeId = fieldProposal.documentTypeId;
  }
  if (touched.recurrence || fieldProposal.recurrence === null) {
    return plan;
  }
  plan.recurrence = fieldProposal.recurrence;
  if (fieldProposal.recurrence === 'NONE') {
    return plan;
  }
  if (fieldProposal.dueAt) {
    plan.dueAt = fieldProposal.dueAt;
  }
  if (fieldProposal.recurrence === 'RECURRING' && fieldProposal.validityMonths !== null) {
    plan.validityMonths = fieldProposal.validityMonths;
  }
  return plan;
}

export type CreateDialogAiField = keyof CreateDialogFillPlan;

const RECURRENCE_GROUP_FIELDS: readonly CreateDialogAiField[] = [
  'recurrence',
  'dueAt',
  'validityMonths',
];

export interface CreateDialogWithdrawalPlan {
  /**
   * `RESET`: il tipo dell'IA si toglie con i default che porta (preavvisi, ricorrenza);
   * `CLEAR_ONLY`: si toglie solo il tipo, perché la scadenza l'ha già decisa l'utente.
   */
  type: 'RESET' | 'CLEAR_ONLY' | null;
  /** Campi di ricorrenza, data e validità che tornano vuoti: erano ancora quelli dell'IA. */
  emptiedFields: CreateDialogAiField[];
}

/**
 * Cosa togliere quando la proposta non vale più (file sostituito o tolto, token rifiutato): solo i
 * valori dell'IA che l'utente non ha toccato; le sue modifiche restano.
 */
export function planCreateDialogWithdrawal(
  unchangedAiFields: readonly CreateDialogAiField[],
  touched: Readonly<Record<CreateDialogFillGroup, boolean>>,
): CreateDialogWithdrawalPlan {
  const emptiedFields = touched.recurrence
    ? []
    : unchangedAiFields.filter((field) => RECURRENCE_GROUP_FIELDS.includes(field));
  if (touched.type || !unchangedAiFields.includes('documentTypeId')) {
    return { type: null, emptiedFields };
  }
  return { type: touched.recurrence ? 'CLEAR_ONLY' : 'RESET', emptiedFields };
}

export interface RenewFillPlan {
  issuedAt?: string;
  dueAt?: string;
}

interface RenewFillInput {
  proposal: DeadlineAiProposal;
  kind: 'RECURRING' | 'ONE_OFF';
  /** Riga "Nessuna scadenza": la riga resta senza data, niente da proporre. */
  isAttachWithoutDate: boolean;
  todayIso: string;
}

/** Rinnovo: data del documento sulle ricorrenti, scadenza stampata sulle uniche (mai nel passato). */
export function planRenewFill(input: RenewFillInput): RenewFillPlan {
  if (input.kind === 'RECURRING') {
    const issuedAt = readConfidentValue(input.proposal.issuedAt);
    return issuedAt ? { issuedAt } : {};
  }
  const expiresAt = readConfidentValue(input.proposal.expiresAt);
  if (input.isAttachWithoutDate || expiresAt === null || expiresAt < input.todayIso) {
    return {};
  }
  return { dueAt: expiresAt };
}
